import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CAROUSEL_AUTOPLAY_TIMEOUT } from '../../constants';
import { useInput, useInterval, useMediaBreakpoints, useWindowEventListener } from '../../../hooks';
export var useCarouselState = function useCarouselState(_ref) {
  var children = _ref.children,
      _ref$numberOfCards = _ref.numberOfCards,
      numberOfCardsProp = _ref$numberOfCards === void 0 ? 0 : _ref$numberOfCards,
      _ref$defaultItemIndex = _ref.defaultItemIndex,
      defaultItemIndex = _ref$defaultItemIndex === void 0 ? 0 : _ref$defaultItemIndex,
      itemIndex = _ref.itemIndex,
      onItemIndexChange = _ref.onItemIndexChange,
      onStateChange = _ref.onStateChange,
      _ref$useAutoplay = _ref.useAutoplay,
      useAutoplay = _ref$useAutoplay === void 0 ? false : _ref$useAutoplay,
      _ref$autoplayTimeout = _ref.autoplayTimeout,
      autoplayTimeout = _ref$autoplayTimeout === void 0 ? CAROUSEL_AUTOPLAY_TIMEOUT : _ref$autoplayTimeout,
      _ref$useKeyboardContr = _ref.useKeyboardControl,
      useKeyboardControl = _ref$useKeyboardContr === void 0 ? false : _ref$useKeyboardContr,
      _ref$disabled = _ref.disabled,
      disabledProp = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$slidesToScroll = _ref.slidesToScroll,
      slidesToScroll = _ref$slidesToScroll === void 0 ? 1 : _ref$slidesToScroll,
      _ref$infinite = _ref.infinite,
      infinite = _ref$infinite === void 0 ? false : _ref$infinite;

  var _useInput = useInput(itemIndex, defaultItemIndex, onItemIndexChange),
      index = _useInput[0],
      setIndex = _useInput[1];

  var numberOfCards = useMediaBreakpoints(numberOfCardsProp);
  var disabled = useMediaBreakpoints(disabledProp);
  var numberOfChildren = useMemo(function () {
    return React.Children.toArray(children).length;
  }, [children]);
  var maxIndex = Math.max(numberOfChildren - Math.floor(numberOfCards), 0);
  var setActiveIndex = useCallback(function (newIndex) {
    var newActiveIndex = infinite ? newIndex : Math.min(Math.max(newIndex, 0), maxIndex);

    if (newActiveIndex !== index) {
      setIndex(newActiveIndex);
    }
  }, [index, maxIndex, infinite]); // Notifies about internal state update

  useEffect(function () {
    onStateChange == null ? void 0 : onStateChange({
      numberOfCards: numberOfCards,
      numberOfChildren: numberOfChildren,
      canMoveBack: infinite ? true : index > 0,
      canMoveForward: infinite ? true : index < numberOfChildren - Math.floor(numberOfCards)
    });
  }, [numberOfCards, numberOfChildren, index]);

  var _useState = useState(false),
      autoplayPaused = _useState[0],
      setAutoplayPaused = _useState[1];

  var isAutoplayPaused = autoplayPaused || !useAutoplay || autoplayTimeout <= 0 || maxIndex <= 0;
  var updateToNextIndex = useCallback(function () {
    var newIndex = infinite ? index + 1 : index + 1 > maxIndex ? 0 : index + 1;
    setIndex(newIndex);
  }, [index, maxIndex, infinite]);
  useInterval(updateToNextIndex, autoplayTimeout, isAutoplayPaused);
  var enableAutoplay = useCallback(function () {
    return setAutoplayPaused(false);
  }, []);
  var disableAutoplay = useCallback(function () {
    return setAutoplayPaused(true);
  }, []);
  var isAutoplayEnabled = !isAutoplayPaused;
  var moveNext = useCallback(function () {
    if (disabled || !infinite && index >= numberOfChildren - Math.floor(numberOfCards)) {
      return;
    }

    var nextIndex = index + slidesToScroll;
    var newIndex = infinite ? nextIndex : Math.min(nextIndex, maxIndex);

    if (newIndex !== index) {
      setIndex(newIndex);
    }
  }, [disabled, index, setIndex, numberOfChildren, numberOfCards, maxIndex, slidesToScroll, infinite]);
  var movePrevious = useCallback(function () {
    if (disabled || !infinite && index <= 0) {
      return;
    }

    var previousIndex = index - slidesToScroll;
    var newIndex = infinite ? previousIndex : Math.max(previousIndex, 0);

    if (newIndex !== index) {
      setIndex(newIndex);
    }
  }, [disabled, index, setIndex, slidesToScroll, infinite]);

  var handleKeyDown = function handleKeyDown(e) {
    if (!useKeyboardControl) {
      return;
    }

    switch (e.key) {
      case 'ArrowRight':
        moveNext();
        break;

      case 'ArrowLeft':
        movePrevious();
        break;

      default:
        break;
    }
  };

  useWindowEventListener('keydown', handleKeyDown, !useKeyboardControl, true);
  return {
    index: index,
    setActiveIndex: setActiveIndex,
    enableAutoplay: enableAutoplay,
    disableAutoplay: disableAutoplay,
    isAutoplayEnabled: isAutoplayEnabled
  };
};
export default useCarouselState;